"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { isMemesQuickVoteMissingDropError } from "@/hooks/memesQuickVote.errors";
import { isMemesQuickVoteDiscoverableDrop, sanitizeStoredAmounts, sanitizeStoredDropIds } from "@/hooks/memesQuickVote.helpers";
import { getMemesQuickVoteDropQueryKey } from "@/hooks/memesQuickVote.query";
import { readStoredNumberArray, readStoredStringArray, useStoredNumberArray, useStoredStringArray, writeStoredNumberArray, writeStoredStringArray } from "@/hooks/memesQuickVote.storageStore";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
const SKIPPED_STORAGE_PREFIX = "memesQuickVoteSkipped";
const AMOUNTS_STORAGE_PREFIX = "memesQuickVoteAmounts";
type UseMemesQuickVoteStorageOptions = {
  readonly contextProfile: string | null | undefined;
  readonly memesWaveId: string | null | undefined;
  readonly proxyId?: string | null | undefined;
};
type UseMemesQuickVoteStorageResult = {
  readonly skippedDropIds: string[];
  readonly recentAmountsByRecency: number[];
  readonly setAndPersistRecentAmounts: (updater: (current: number[]) => number[]) => void;
  readonly setAndPersistSkippedDropIds: (updater: (current: string[]) => string[]) => void;
};
type UseMemesQuickVoteSkippedDropIdsResult = Pick<UseMemesQuickVoteStorageResult, "setAndPersistSkippedDropIds" | "skippedDropIds">;
const getStorageKey = (prefix: string, memesWaveId: string | null | undefined, contextProfile: string | null | undefined): string | null => {
  if (!memesWaveId || !contextProfile) {
    return null;
  }
  return `${prefix}:${memesWaveId}:${contextProfile}`;
};
const areNumberArraysEqual = (left: readonly number[], right: readonly number[]): boolean => left.length === right.length && left.every((value, index) => value === right[index]);
const areStringArraysEqual = (left: readonly string[], right: readonly string[]): boolean => left.length === right.length && left.every((value, index) => value === right[index]);
const getEffectiveMemesQuickVoteSkippedDropIds = ({
  contextProfile,
  memesWaveId,
  proxyId,
  queryClient,
  skippedDropIds
}: {
  readonly contextProfile: string | null | undefined;
  readonly memesWaveId: string | null | undefined;
  readonly proxyId: string | null | undefined;
  readonly queryClient: ReturnType<typeof useQueryClient>;
  readonly skippedDropIds: readonly string[];
}): string[] => skippedDropIds.filter(dropId => {
  const queryKey = getMemesQuickVoteDropQueryKey({
    contextProfile,
    dropId,
    proxyId
  });
  const cachedDrop = queryClient.getQueryData<ApiDrop>(queryKey);
  if (cachedDrop !== undefined) {
    return isMemesQuickVoteDiscoverableDrop({
      drop: cachedDrop,
      waveId: memesWaveId
    });
  }
  const queryState = queryClient.getQueryState(queryKey);
  if (queryState?.status !== "error") {
    return true;
  }
  return !isMemesQuickVoteMissingDropError(queryState.error);
});
export const useMemesQuickVoteSkippedDropIds = t0 => {
  const $ = _c(18);
  const {
    contextProfile,
    memesWaveId,
    proxyId
  } = t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== contextProfile || $[1] !== memesWaveId) {
    t1 = getStorageKey(SKIPPED_STORAGE_PREFIX, memesWaveId, contextProfile);
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const skippedStorageKey = t1;
  const storedSkippedDropIds = useStoredStringArray(skippedStorageKey, sanitizeStoredDropIds);
  let t2;
  if ($[3] !== contextProfile || $[4] !== memesWaveId || $[5] !== proxyId || $[6] !== queryClient || $[7] !== skippedStorageKey) {
    t2 = updater => {
      const storedCurrent = readStoredStringArray(skippedStorageKey, sanitizeStoredDropIds);
      const current = getEffectiveMemesQuickVoteSkippedDropIds({
        contextProfile,
        memesWaveId,
        proxyId,
        queryClient,
        skippedDropIds: storedCurrent
      });
      const next = sanitizeStoredDropIds(updater([...current]));
      if (areStringArraysEqual(storedCurrent, next)) {
        return;
      }
      writeStoredStringArray(skippedStorageKey, next);
    };
    $[3] = contextProfile;
    $[4] = memesWaveId;
    $[5] = proxyId;
    $[6] = queryClient;
    $[7] = skippedStorageKey;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const setAndPersistSkippedDropIds = t2;
  let t3;
  if ($[9] !== contextProfile || $[10] !== memesWaveId || $[11] !== proxyId || $[12] !== queryClient || $[13] !== storedSkippedDropIds) {
    t3 = getEffectiveMemesQuickVoteSkippedDropIds({
      contextProfile,
      memesWaveId,
      proxyId,
      queryClient,
      skippedDropIds: storedSkippedDropIds
    });
    $[9] = contextProfile;
    $[10] = memesWaveId;
    $[11] = proxyId;
    $[12] = queryClient;
    $[13] = storedSkippedDropIds;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const skippedDropIds = t3;
  let t4;
  if ($[15] !== setAndPersistSkippedDropIds || $[16] !== skippedDropIds) {
    t4 = {
      setAndPersistSkippedDropIds,
      skippedDropIds
    };
    $[15] = setAndPersistSkippedDropIds;
    $[16] = skippedDropIds;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  return t4;
};
export const useMemesQuickVoteStorage = t0 => {
  const $ = _c(14);
  const {
    contextProfile,
    memesWaveId,
    proxyId
  } = t0;
  let t1;
  if ($[0] !== contextProfile || $[1] !== memesWaveId) {
    t1 = getStorageKey(AMOUNTS_STORAGE_PREFIX, memesWaveId, contextProfile);
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const amountsStorageKey = t1;
  let t2;
  if ($[3] !== contextProfile || $[4] !== memesWaveId || $[5] !== proxyId) {
    t2 = {
      contextProfile,
      memesWaveId,
      proxyId
    };
    $[3] = contextProfile;
    $[4] = memesWaveId;
    $[5] = proxyId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    setAndPersistSkippedDropIds,
    skippedDropIds
  } = useMemesQuickVoteSkippedDropIds(t2);
  const recentAmountsByRecency = useStoredNumberArray(amountsStorageKey, sanitizeStoredAmounts);
  let t3;
  if ($[7] !== amountsStorageKey) {
    t3 = updater => {
      const current = readStoredNumberArray(amountsStorageKey, sanitizeStoredAmounts);
      const next = sanitizeStoredAmounts(updater([...current]));
      if (areNumberArraysEqual(current, next)) {
        return;
      }
      writeStoredNumberArray(amountsStorageKey, next);
    };
    $[7] = amountsStorageKey;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const setAndPersistRecentAmounts = t3;
  let t4;
  if ($[9] !== recentAmountsByRecency || $[10] !== setAndPersistRecentAmounts || $[11] !== setAndPersistSkippedDropIds || $[12] !== skippedDropIds) {
    t4 = {
      skippedDropIds,
      recentAmountsByRecency,
      setAndPersistRecentAmounts,
      setAndPersistSkippedDropIds
    };
    $[9] = recentAmountsByRecency;
    $[10] = setAndPersistRecentAmounts;
    $[11] = setAndPersistSkippedDropIds;
    $[12] = skippedDropIds;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
};